// @flow
import * as React from 'react';

import { ARRAY_ERROR, type FormValuesShape } from 'final-form';
import intersection from 'lodash/intersection';
import { useForm, useFormState } from 'react-final-form';

import { useTranslator } from '@gandi/translate';
import { Base, Button } from '@gandi/ui';

import { useModifiedSinceLastSubmit } from '../hooks/useModifiedSinceLastSubmit';

import { FormError } from './FormError';
import { Header } from './Header';

export type UseStepWizardResult = {
  currentStep: number,
  nextStep: () => void,
  previousStep: () => void,
  isStart: boolean,
  isEnd: boolean,
};

const useStepWizard = (maxStep: number): UseStepWizardResult => {
  const [currentStep, setStep] = React.useState(0);

  return {
    currentStep,
    nextStep: () =>
      setStep(prev => {
        if (prev + 1 <= maxStep) {
          return prev + 1;
        }

        return prev;
      }),
    previousStep: () =>
      setStep(prev => {
        if (prev === 0) {
          return prev;
        }

        return prev - 1;
      }),
    isStart: currentStep === 0,
    isEnd: currentStep + 1 === maxStep,
  };
};

type UseStepStatusResult = {
  valid: ?boolean,
  validating: ?boolean,
  modifiedSinceLastSubmit: ?boolean,
  submitting: ?boolean,
  stepValid: boolean,
};
const useStepStatus = (stepFields: Array<string>): UseStepStatusResult => {
  const { valid, validating, submitting, errors } =
    useFormState<FormValuesShape>({
      subscription: {
        valid: true,
        validating: true,
        errors: true,
        submitting: true,
      },
    });

  const modifiedSinceLastSubmit = useModifiedSinceLastSubmit();

  const filteredErrorFields = Object.entries(errors || {})
    .filter(([, value]) => {
      if (!Array.isArray(value)) {
        return true;
      }

      if (value.length > 0) {
        return true;
      }

      // This is how error is stored for an array of inputs:
      // https://github.com/final-form/react-final-form-arrays/blob/a19c686b60e54720e9bd09cc559f94dfdcb83f87/src/useFieldArray.js#L50-L53
      // $FlowFixMe[incompatible-use]
      return !!value[ARRAY_ERROR];
    })
    .map(([key]) => key);

  const stepValid =
    !errors ||
    !stepFields ||
    intersection(filteredErrorFields, stepFields).length === 0;

  return {
    valid,
    validating,
    modifiedSinceLastSubmit,
    submitting,
    stepValid,
  };
};

type WizardNavigationProps = {
  ...$Rest<
    UseStepWizardResult,
    {
      currentStep: number,
    },
  >,
  stepFields: void | Array<string>,
};

function WizardNavigation({
  isStart,
  isEnd,
  previousStep,
  nextStep,
  stepFields = [],
}: WizardNavigationProps) {
  const { __ } = useTranslator();
  const form = useForm<FormValuesShape>();
  const { valid, validating, modifiedSinceLastSubmit, stepValid, submitting } =
    useStepStatus(stepFields);

  // Pause validation (before unmounting) to prevent form to try to validate
  // unmounted fields (cf. T101972)
  // cf. https://github.com/final-form/react-final-form/pull/1018
  const handleClickPrevious = () => {
    form.pauseValidation();
    previousStep();
  };
  const handleClickNext = () => {
    form.pauseValidation();
    nextStep();
  };

  return (
    <Base flex gap="small">
      {!isStart && (
        <Button type="button" onClick={handleClickPrevious} kind="outline">
          {__('Previous')}
        </Button>
      )}
      {!isEnd && (
        <Button
          type="button"
          onClick={handleClickNext}
          disabled={!stepValid}
          loading={!!validating}
        >
          {__('Next')}
        </Button>
      )}
      {isEnd && (
        <Button
          type="submit"
          // the button will be enabled if a submit error happenned but the form has been edited
          disabled={!valid && !modifiedSinceLastSubmit}
          loading={!!submitting}
        >
          {__('Add to cart')}
        </Button>
      )}
    </Base>
  );
}

type StepProps = {
  children: React.Node,
  stepFields?: Array<string>,
};
export function Step({ children }: StepProps): React.Node {
  const form = useForm<FormValuesShape>();
  React.useEffect(() => {
    // resume validation while Steps are mounting & unmounting to prevent form to try to validate unmounted fields (cf. T101972)
    // cf. https://github.com/final-form/react-final-form/pull/1018
    form.resumeValidation();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return children;
}
type WizardProps = {
  children: React$Element<typeof Step> | React$Element<typeof Step>[],
};

/**
 * Display a form wizard:
 *
 * ```
 * <Form onSubmit={onSubmit}>
 *   {({ handleSubmit }) => (
 *     <form onSubmit={handleSubmit}>
 *       <Wizard>
 *         <Step>Step 1</Step>
 *         <Step>Step 2</Step>
 *       </Wizard>
 *     </form>
 *   )}
 * </Form>
 * ```
 *
 * For now, the navigation is included. Submit button will be disabled if form
 * is invalid or submitting.
 *
 * Next button will be disabled if current step is invalid. To do so, list
 * fields included in the step:
 * ```
 * <Step stepFields={['foo', 'bar']}>
 *   <Field name="foo" required />
 *   <Field name="bar" required />
 *   <Field name="baz" required /> // /!\ Not listed in stepFields: careful
 * </Step>
 * ```
 *
 * Note: last step does not require `stepFields` prop, the form must be globally
 * valid to submit
 */
export function Wizard({ children }: WizardProps): React.MixedElement {
  const steps: Array<React$Element<typeof Step>> =
    React.Children.toArray(children);
  const { currentStep, ...navParams } = useStepWizard(steps.length);
  const activeStep = steps[currentStep];
  // $FlowFixMe[prop-missing]
  const { stepFields } = activeStep.props;

  return (
    <>
      <Base marginBottom="base">
        <Header currentStep={currentStep + 1} />
      </Base>
      <Base marginBottom="base">
        <FormError />
      </Base>
      <Base marginBottom="base">{activeStep}</Base>
      <WizardNavigation {...navParams} stepFields={stepFields} />
    </>
  );
}
